import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useAemConfig } from '@as-react/common-components';
import { ProductImageSizesPortraitEnum, ProductTypes, StockLocation, } from '@as-react/common-types';
import { isOneSizeProduct, showPriceDiscount } from '@as-react/common-utils';
import { XIcon } from '@yonderland/andes-icons';
import { Box, Grid, IconButton, ProductTile, ProductTileHorizontal, ProductTileImage, ProductTileTitle, QuantitySelector, Stack, StatusIndicator, TextAlpha, } from '@yonderland/andes-react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { I18N_NAMESPACES } from '../../../constants/i18nConstants';
import { useBasketOverviewConfig } from '../../../context/ComponentConfigContext';
import { usePriceViewModel } from '../../../hooks/usePriceViewModel';
import { basketActions } from '../../../redux/basket/actions/BasketActions.creators';
import { getDeliveryExplanation, isHomeDelivery } from '../../../util/DeliveryPromiseUtil';
import { getImageUrlForBasketTile } from '../../../util/ImageUtil';
import { getProductDetailUrl, getSizeColorLabel } from '../../../util/ProductUtil';
import AsNumeral from '../../../vendors/asNumeral';
import { DeliveryPromiseMessage } from '../../delivery-promise/DeliveryPromiseMessage';
import { WishlistButton } from '../../wishlist-button/WishlistButton';
export const ProductTileBasketOverview = ({ product, isDisabled }) => {
    var _a, _b, _c, _d;
    const { t } = useTranslation([I18N_NAMESPACES.navigation, I18N_NAMESPACES.product, I18N_NAMESPACES.checkout]);
    const { currency, root, productImageConfig, feature_toggles: { lowest_price_all }, oneSizeProductCode, } = useAemConfig();
    const dispatch = useDispatch();
    const { general: { showMultilineDeliveryPromise }, } = useBasketOverviewConfig();
    const image = getImageUrlForBasketTile(product, productImageConfig, ProductImageSizesPortraitEnum.basket);
    const getRequestObject = (quantity) => ({
        quantity,
        sku: String(product.sku),
        productId: String(product.productId),
        colorId: product.colorId,
        productCode: product.productCode,
        productPriceManual: product.priceManual,
        additionalProperties: product.additionalProperties,
    });
    const priceActual = {
        formattedPrice: AsNumeral.formatAsCurrency(product.totalPrice),
    };
    const pricePrevious = product.productHasDiscount && !!product.totalPreviousPrice
        ? {
            formattedPrice: AsNumeral.formatAsCurrency(product.totalPreviousPrice),
            priceLabel: ((_a = product.prices) === null || _a === void 0 ? void 0 : _a.RRP) ? t(`${I18N_NAMESPACES.product}:product.price.rrp`) : undefined,
        }
        : undefined;
    const priceReference = product.productHasDiscount && !!product.lowestPrice
        ? {
            formattedPrice: AsNumeral.formatAsCurrency(product.lowestPrice),
        }
        : undefined;
    const priceViewModel = usePriceViewModel({
        prices: product.prices,
        hasDiscount: Boolean(product.productHasDiscount),
    }, {
        priceActual,
        pricePrevious,
        priceReference,
    }, lowest_price_all);
    const discountAmount = AsNumeral.formatAsCurrency(product.totalProductDiscountAmount);
    const deliveryPromiseExplanation = product.productType !== ProductTypes.E_VOUCHER
        ? getDeliveryExplanation({ stockLocation: (_b = product.additionalProperties) === null || _b === void 0 ? void 0 : _b.stockLocation })
        : undefined;
    const getDeliveryPromise = () => {
        var _a;
        if (isDisabled) {
            return (_jsx(StatusIndicator, { status: "incomplete", align: "end", children: t(`${I18N_NAMESPACES.checkout}:checkout.delivery_promise.product.cannot_be_collected`) }));
        }
        if (product.productType === ProductTypes.E_VOUCHER) {
            return _jsx(TextAlpha, { children: t(`${I18N_NAMESPACES.checkout}:checkout.mailbox`) });
        }
        if (!((_a = product.additionalProperties) === null || _a === void 0 ? void 0 : _a.deliveryMessage))
            return null;
        const { additionalProperties } = product;
        return (_jsx(DeliveryPromiseMessage, { touchPoint: "product-tile", option: isHomeDelivery(additionalProperties.deliveryOptionName) ? 'hd' : 'cc', state: additionalProperties.deliveryPromiseCutoff || additionalProperties.stockLocation === StockLocation.IN_STORE
                ? 'direct'
                : 'delayed', type: additionalProperties.deliveryOptionName, message: additionalProperties.deliveryMessage, cutoff: additionalProperties.deliveryPromiseCutoff, explanation: deliveryPromiseExplanation, testId: "basket_product_delivery_promise" }));
    };
    const handleProductRemove = () => {
        dispatch(basketActions.createRemoveProductAndUpdate(getRequestObject(product.quantity), true, product));
    };
    const handleQuantityUpdate = (quantity, action) => {
        if (action === 'decrease') {
            quantity === 0
                ? handleProductRemove()
                : dispatch(basketActions.createDecreaseProductQuantityAction(getRequestObject(1), true));
        }
        else if (action === 'increase') {
            dispatch(basketActions.createIncreaseProductQuantityAction(getRequestObject(1), true));
        }
        else if (action === 'update') {
            dispatch(basketActions.createUpdateQuantityCustomAction(getRequestObject(quantity), true));
        }
    };
    const deliveryPromise = showMultilineDeliveryPromise ? getDeliveryPromise() : null;
    const productDetailUrl = getProductDetailUrl({
        seoUrl: product.seoUrl,
        colourId: product.colorId,
    }, root);
    const getSizeOptions = (product) => {
        var _a;
        if ((product.productType === ProductTypes.PRODUCT || product.productType === ProductTypes.GIFT_CARD) &&
            !!((_a = product.sizes) === null || _a === void 0 ? void 0 : _a.length)) {
            return product.sizes.sort((sizeA, sizeB) => sizeA.order - sizeB.order);
        }
        return [];
    };
    const sizeOptions = getSizeOptions(product);
    const oneSizeProduct = isOneSizeProduct(product.sizes, oneSizeProductCode);
    const hasSizes = !oneSizeProduct && (sizeOptions === null || sizeOptions === void 0 ? void 0 : sizeOptions.length) > 0;
    return (_jsxs(ProductTileHorizontal, { testId: "basket_product", product: Object.assign(Object.assign({ alt: [product.brand, product.description, product.colorDescription].join(' '), image,
            currency }, priceViewModel), { priceCaveat: product.productHasDiscount && showPriceDiscount(product.prices, lowest_price_all)
                ? {
                    text: `${t(`${I18N_NAMESPACES.checkout}:checkout.basket.discount.label`)} ${currency}${discountAmount} / ${(_c = product.productDiscountPercentage) === null || _c === void 0 ? void 0 : _c.toString()}%`,
                    isCommercial: true,
                }
                : undefined, title: product.description, brand: product.brand }), children: [_jsx(ProductTile.Cell, { section: "media", children: _jsx(ProductTileImage, { height: {
                        lg: 180,
                        xs: 120,
                    }, width: {
                        lg: 120,
                        xs: 80,
                    }, url: productDetailUrl }) }), _jsx(ProductTile.Cell, { section: "content", children: _jsxs(Grid, { gap: "4", columns: 1, fullHeight: true, children: [_jsxs(Stack, { gap: "4", wrap: "nowrap", align: "start", children: [_jsx(Box, { flex: {
                                        grow: 1,
                                    }, children: _jsxs(Stack, { direction: "col", gap: "2", children: [_jsx(ProductTileTitle, { brandOnTheSameLine: true, url: productDetailUrl }), _jsx(TextAlpha, { color: "subtle", testId: "basket_product_color", children: getSizeColorLabel(Object.assign(Object.assign({}, product), { sizeDescription: hasSizes ? product.sizeDescription : '.' }), t) })] }) }), _jsxs(Stack, { gap: "1", wrap: "nowrap", children: [_jsx(WishlistButton, { productCode: product.productCode, productId: Number(product.productId), colourId: product.colorId, skuId: (_d = product.sku) === null || _d === void 0 ? void 0 : _d.toString(), testId: "basket_product_add_to_wishlist" }), _jsx(IconButton, { icon: {
                                                source: XIcon,
                                            }, onClick: handleProductRemove, size: "sm", theme: "neutral", variant: "plain", testId: "basket_product_remove" })] })] }), _jsxs(Grid, { columns: 6, gap: "4", children: [_jsx(Grid.Cell, { rowOffset: { md: 2 }, offset: 1, span: {
                                        xs: 6,
                                        md: 3,
                                        xl: 2,
                                    }, align: "end", children: _jsx(QuantitySelector, { testId: "basket_product_amount", onUpdate: handleQuantityUpdate, quantity: product.quantity }) }), _jsx(Grid.Cell, { align: "end", justify: "end", offset: {
                                        md: 4,
                                    }, rowOffset: {
                                        md: 1,
                                    }, rowSpan: {
                                        md: 2,
                                    }, span: {
                                        xs: 6,
                                        md: 3,
                                        xl: 4,
                                    }, children: _jsxs(Stack, { direction: "col", gap: "1", align: "end", children: [deliveryPromise, _jsx(ProductTile.Price, { align: "end" })] }) })] })] }) })] }));
};
